import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "components/ui/alert";

interface ProcedureCodePromptProps {
  open: boolean;
  questionText: string;
  onConfirm: (procedureCode: string) => void;
  onCancel: () => void;
}

const ProcedureCodePrompt: React.FC<ProcedureCodePromptProps> = ({
  open,
  questionText,
  onConfirm,
  onCancel,
}) => {
  const [procedureCode, setProcedureCode] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      setProcedureCode("");
      setError(null);
    }
  }, [open]);

  const handleConfirm = () => {
    const trimmed = procedureCode.trim();

    // Validate required field
    if (!trimmed) {
      setError("Procedure code is required");
      return;
    }

    // Basic validation: alphanumeric, dashes, dots allowed
    // Common procedure code formats: CPT codes (5 digits), dental codes (D####), etc.
    if (!/^[A-Za-z0-9.-]+$/.test(trimmed)) {
      setError("Procedure code can only contain letters, numbers, dots, and dashes");
      return;
    }

    // Normalize to uppercase and confirm
    onConfirm(trimmed.toUpperCase());
  };

  const handleCancel = () => {
    setProcedureCode("");
    setError(null);
    onCancel();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProcedureCode(e.target.value);
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && procedureCode.trim()) {
      e.preventDefault();
      handleConfirm();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && handleCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Enter Procedure Code</DialogTitle>
          <DialogDescription>
            This question requires a procedure code. Please enter the code you want to use.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Display the question text being customized */}
          <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-sm text-gray-700">{questionText}</p>
          </div>

          {/* Procedure code input */}
          <div className="space-y-2">
            <label htmlFor="procedure-code" className="text-sm font-medium text-gray-700">
              Procedure Code <span className="text-red-500">*</span>
            </label>
            <Input
              id="procedure-code"
              type="text"
              placeholder="e.g., 90791, D0120, 97110"
              value={procedureCode}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              className={error ? "border-red-500 focus:ring-red-500" : ""}
              autoFocus
            />
            {error && (
              <Alert variant="destructive" className="py-2">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-sm">{error}</AlertDescription>
              </Alert>
            )}
            <p className="text-xs text-gray-500">
              Enter the procedure code this question applies to (e.g., CPT codes, dental codes)
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            className="bg-[#005151] hover:bg-[#004040] text-white"
            disabled={!procedureCode.trim()}
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProcedureCodePrompt;
